import { Link } from '@tanstack/react-router';
import { Archive, Copy, Edit3, RotateCcw } from 'lucide-react';
import { DashboardTableRowActionItem, DashboardTableRowActions } from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { PlacementStatus, type Placement } from '../../model';
import type { PlacementColumnsOptions } from './placementsTableColumns';
import { copyToClipboard } from '../../../../../lib/clipboard';

export function PlacementActions({
  placement,
  options,
}: {
  placement: Placement;
  options: PlacementColumnsOptions;
}) {
  const pending = options.statusPendingIds.has(placement.id);
  if (!options.canEdit && !options.canArchive) return null;
  return (
    <DashboardTableRowActions label={dashboardT('placements.actionsFor', { name: placement.name })}>
      {options.canEdit ? (
        <DashboardTableRowActionItem asChild>
          <Link to="/supply/placements/edit/$placementId" params={{ placementId: placement.id }}>
            <Edit3 className="h-4 w-4" aria-hidden="true" />
            {dashboardT('placements.edit')}
          </Link>
        </DashboardTableRowActionItem>
      ) : null}
      {options.canArchive ? (
        <DashboardTableRowActionItem
          disabled={pending}
          onSelect={() => {
            options.onArchive(placement);
          }}
        >
          {placement.status === PlacementStatus.Archived ? (
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Archive className="h-4 w-4" aria-hidden="true" />
          )}
          {dashboardT(
            placement.status === PlacementStatus.Archived
              ? 'placements.unarchive'
              : 'placements.archive',
          )}
        </DashboardTableRowActionItem>
      ) : null}
    </DashboardTableRowActions>
  );
}

export function PlacementIdCell({ value }: { value: string | undefined }) {
  if (!value) return null;
  return (
    <div className="group flex min-w-0 items-center gap-1">
      <span className="truncate">{value}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 focus:opacity-100"
        aria-label={dashboardT('placements.copyId')}
        onClick={() => {
          void copyToClipboard(value, {
            successMessage: dashboardT('placements.copied'),
            errorMessage: dashboardT('placements.copyError'),
          });
        }}
      >
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </div>
  );
}
